import type { ReactNode } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { useVenue } from "../lib/venue";
import { isBookingVenue } from "../lib/venue-type";

// Bottom tab bar. Venue-type-aware: every venue gets Home + Profile; the middle
// slots surface the highest-value destinations for that venue, its primary
// action (Book for studios, Events for bars), Rewards (when loyalty is on), and
// Menu/Noticeboard as room allows. Capped at 5 tabs. Hidden on auth screens.

const ICON = {
  home: {
    outline: "M2.25 12 11.204 3.045c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25",
    fill: "M11.47 3.84a.75.75 0 0 1 1.06 0l8.69 8.69a.75.75 0 1 0 1.06-1.06l-8.689-8.69a2.25 2.25 0 0 0-3.182 0l-8.69 8.69a.75.75 0 1 0 1.061 1.06l8.69-8.69ZM12 5.432l8.159 8.159c.03.03.06.058.091.086v6.198c0 1.035-.84 1.875-1.875 1.875H15a.75.75 0 0 1-.75-.75v-4.5a.75.75 0 0 0-.75-.75h-3a.75.75 0 0 0-.75.75V21a.75.75 0 0 1-.75.75H5.625a1.875 1.875 0 0 1-1.875-1.875v-6.198a2.29 2.29 0 0 0 .091-.086L12 5.43Z",
  },
  calendar: {
    outline: "M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5",
    fill: "M6.75 2.25A.75.75 0 0 1 7.5 3v1.5h9V3A.75.75 0 0 1 18 3v1.5h.75a3 3 0 0 1 3 3v11.25a3 3 0 0 1-3 3H5.25a3 3 0 0 1-3-3V7.5a3 3 0 0 1 3-3H6V3a.75.75 0 0 1 .75-.75Zm13.5 9a1.5 1.5 0 0 0-1.5-1.5H5.25a1.5 1.5 0 0 0-1.5 1.5v7.5a1.5 1.5 0 0 0 1.5 1.5h13.5a1.5 1.5 0 0 0 1.5-1.5v-7.5Z",
  },
  star: {
    outline: "M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z",
    fill: "M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.006 5.404.434c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.434 2.082-5.005Z",
  },
  menu: {
    outline: "M15.75 10.5V6a3.75 3.75 0 1 0-7.5 0v4.5m11.356-1.993 1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 0 1-1.12-1.243l1.264-12A1.125 1.125 0 0 1 5.513 7.5h12.974c.576 0 1.059.435 1.119 1.007ZM8.625 10.5a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm7.5 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z",
    fill: "M7.5 6v.75H5.513c-.96 0-1.764.724-1.865 1.679l-1.263 12A1.875 1.875 0 0 0 4.25 22.5h15.5a1.875 1.875 0 0 0 1.865-2.071l-1.263-12a1.875 1.875 0 0 0-1.865-1.679H16.5V6a4.5 4.5 0 1 0-9 0ZM12 3a3 3 0 0 0-3 3v.75h6V6a3 3 0 0 0-3-3Zm-3 8.25a3 3 0 1 0 6 0v-.75a.75.75 0 0 1 1.5 0v.75a4.5 4.5 0 1 1-9 0v-.75a.75.75 0 0 1 1.5 0v.75Z",
  },
  board: {
    outline: "M12 7.5h1.5m-1.5 3h1.5m-7.5 3h7.5m-7.5 3h7.5m3-9h3.375c.621 0 1.125.504 1.125 1.125V18a2.25 2.25 0 0 1-2.25 2.25M16.5 7.5V18a2.25 2.25 0 0 0 2.25 2.25M16.5 7.5V4.875c0-.621-.504-1.125-1.125-1.125H4.125C3.504 3.75 3 4.254 3 4.875V18a2.25 2.25 0 0 0 2.25 2.25h13.5M6 7.5h3v3H6v-3Z",
    fill: "M4.125 3C3.089 3 2.25 3.84 2.25 4.875V18a3 3 0 0 0 3 3h15a3 3 0 0 1-3-3V4.875C17.25 3.839 16.41 3 15.375 3H4.125ZM12 9.75a.75.75 0 0 0 0 1.5h1.5a.75.75 0 0 0 0-1.5H12Zm-.75-2.25a.75.75 0 0 1 .75-.75h1.5a.75.75 0 0 1 0 1.5H12a.75.75 0 0 1-.75-.75ZM6 12.75a.75.75 0 0 0 0 1.5h7.5a.75.75 0 0 0 0-1.5H6Zm-.75 3.75a.75.75 0 0 1 .75-.75h7.5a.75.75 0 0 1 0 1.5H6a.75.75 0 0 1-.75-.75ZM6 6.75a.75.75 0 0 0-.75.75v3c0 .414.336.75.75.75h3a.75.75 0 0 0 .75-.75v-3A.75.75 0 0 0 9 6.75H6Z",
  },
  profile: {
    outline: "M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z",
    fill: "M7.5 6a4.5 4.5 0 1 1 9 0 4.5 4.5 0 0 1-9 0ZM3.751 20.105a8.25 8.25 0 0 1 16.498 0 .75.75 0 0 1-.437.695A18.683 18.683 0 0 1 12 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 0 1-.437-.695Z",
  },
  ticket: {
    outline: "M16.5 6v.75m0 3v.75m0 3v.75m0 3V18m-9-1.5h5.25M7.5 15h3M3.375 5.25c-.621 0-1.125.504-1.125 1.125v3.026a2.999 2.999 0 0 1 0 5.198v3.026c0 .621.504 1.125 1.125 1.125h17.25c.621 0 1.125-.504 1.125-1.125v-3.026a2.999 2.999 0 0 1 0-5.198V6.375c0-.621-.504-1.125-1.125-1.125H3.375Z",
    fill: "M4.5 4.5a3 3 0 0 0-3 3v.75h.75a3.75 3.75 0 0 1 0 7.5h-.75v.75a3 3 0 0 0 3 3h15a3 3 0 0 0 3-3v-.75h-.75a3.75 3.75 0 0 1 0-7.5h.75v-.75a3 3 0 0 0-3-3h-15Zm12 3a.75.75 0 0 0-1.5 0v.75a.75.75 0 0 0 1.5 0V7.5Zm0 3.75a.75.75 0 0 0-1.5 0v.75a.75.75 0 0 0 1.5 0v-.75Zm0 3.75a.75.75 0 0 0-1.5 0v.75a.75.75 0 0 0 1.5 0v-.75Z",
  },
};

type Glyph = keyof typeof ICON;
type NavItem = { to: string; label: string; glyph: Glyph; exact?: boolean };

function glyphEl(glyph: Glyph, filled: boolean): ReactNode {
  const g = ICON[glyph];
  return filled ? (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="bn-icon">
      <path fillRule="evenodd" d={g.fill} clipRule="evenodd" />
    </svg>
  ) : (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="bn-icon">
      <path strokeLinecap="round" strokeLinejoin="round" d={g.outline} />
    </svg>
  );
}

export function BottomNav() {
  const { data: venueData } = useVenue();
  const venue = venueData?.venue;

  const route = useRouterState({ select: (s) => s.location.pathname });
  const onAuthScreen =
    route === "/sign-in" || route === "/sign-up" ||
    route === "/forgot-password" || route === "/reset-password";
  if (onAuthScreen) return null;

  const isStudio = isBookingVenue(venue?.archetype);

  // Home + Profile always; fill ≤3 middle slots by value for this venue.
  const middle: NavItem[] = [];
  if (isStudio) {
    middle.push({ to: "/book", label: "Book", glyph: "calendar" });
    // Studios that sell ticketed events get an Events tab too.
    if (venue?.hasPublishedEvents) middle.push({ to: "/events", label: "Events", glyph: "ticket" });
  } else {
    middle.push({ to: "/events", label: "Events", glyph: "calendar" });
  }
  if (venue?.loyaltyEnabled) middle.push({ to: "/rewards", label: "Benefits", glyph: "star" });
  if (venue?.menuEnabled) middle.push({ to: "/menu", label: "Menu", glyph: "menu" });
  if (middle.length < 3) middle.push({ to: "/noticeboard", label: "Noticeboard", glyph: "board" });

  const items: NavItem[] = [
    { to: "/", label: "Home", glyph: "home", exact: true },
    ...middle.slice(0, 3),
    { to: "/account", label: "Profile", glyph: "profile" },
  ];

  return (
    <nav className="bottom-nav" aria-label="Main">
      {items.map((item) => {
        const isActive = item.exact ? route === item.to : route.startsWith(item.to);
        return (
          <Link key={item.to} to={item.to} className={`bottom-nav-item ${isActive ? "active" : ""}`}>
            {glyphEl(item.glyph, isActive)}
            <span className="bottom-nav-label">{item.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}
